import { toISOStringYYYYMMDD } from '../../../../TaskScheduler/TaskSchedulerUtils'
import TaskSettingsModal from '../../../../TaskScheduler/TaskSettingsModal/TaskSettingsModal'
import { RobotApi } from 'api/RobotApi'
import { MissionJobRead, MissionJobTypeEnum, OdysseyRead } from 'api/schema'
import clsx from 'clsx'
import ActionsMenu, { ActionsMenuItem } from 'components/ActionsMenu/ActionsMenu'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import { ReactComponent as RemoveSvg } from 'images/icons/delete.svg'
import React from 'react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { selectRobot } from 'store/selectors/robot'
import { PATH } from 'utils/constants'
import getTestId from 'utils/getTestId'

import style from './RobotTasks.module.css'

type CombinedTask = (MissionJobRead | OdysseyRead) & { isOdyssey?: boolean }

const RobotTasks = () => {
  const robot = useSelector(selectRobot)
  const [combinedTasks, setCombinedTasks] = React.useState<CombinedTask[]>([])
  const { t } = useTranslation()
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [selectedTask, setSelectedTask] = React.useState<CombinedTask | null>(null)
  const [deleteTaskDialog, setDeleteTaskDialog] = useState(false)
  const previousTasksKeyRef = useRef<string>('')

  useEffect(() => {
    if (robot.robotInfo?.robotRaw) {
      // Create a key that includes both IDs and statuses to detect changes
      const missionsJobsKey =
        robot.robotInfo.robotRaw.missions_jobs
          ?.map(t => `${t.id}:${t.status || ''}`)
          .sort()
          .join(',') || ''
      const odysseysKey =
        robot.robotInfo.robotRaw.odysseys
          ?.map(o => `${o.id}:${o.status || ''}`)
          .sort()
          .join(',') || ''
      const currentTasksKey = `${missionsJobsKey}|${odysseysKey}`

      // Skip update if data hasn't changed (including statuses)
      if (previousTasksKeyRef.current === currentTasksKey) {
        return
      }

      previousTasksKeyRef.current = currentTasksKey

      const tasks = robot.robotInfo.robotRaw.missions_jobs?.map(t => ({ ...t, isOdyssey: false })) || []
      const odysseys = robot.robotInfo.robotRaw.odysseys?.map(o => ({ ...o, isOdyssey: true })) || []
      const allTasks = [...tasks, ...odysseys]

      // Remove duplicates by ID and type (missions_jobs and odysseys can have the same IDs)
      // Use Map for more reliable deduplication
      const tasksMap = new Map<string, CombinedTask>()
      allTasks.forEach(task => {
        const key = `${task.isOdyssey ? 'odyssey' : 'mission_job'}_${task.id}`
        // If task already exists, take the newer one (with later updated_at or created_at)
        const existing = tasksMap.get(key)
        if (!existing) {
          tasksMap.set(key, task)
        } else {
          const existingDate = existing.updated_at || existing.created_at
          const currentDate = task.updated_at || task.created_at
          if (currentDate > existingDate) {
            tasksMap.set(key, task)
          }
        }
      })

      const uniqueTasks = Array.from(tasksMap.values())

      // Update state if task count, IDs, or statuses have changed
      setCombinedTasks(prevTasks => {
        const prevKey = prevTasks
          .map(t => `${t.isOdyssey ? 'odyssey' : 'mission_job'}_${t.id}:${t.status || ''}`)
          .sort()
          .join(',')
        const newKey = uniqueTasks
          .map(t => `${t.isOdyssey ? 'odyssey' : 'mission_job'}_${t.id}:${t.status || ''}`)
          .sort()
          .join(',')

        // If data hasn't changed (including statuses), keep previous state
        if (prevKey === newKey && prevTasks.length === uniqueTasks.length) {
          return prevTasks
        }

        // If new set contains all tasks from previous + new ones (superset),
        // use the new set completely (it's more complete and up-to-date)
        if (uniqueTasks.length >= prevTasks.length) {
          const prevTaskKeys = new Set(prevTasks.map(t => `${t.isOdyssey ? 'odyssey' : 'mission_job'}_${t.id}`))
          const newTaskKeys = new Set(uniqueTasks.map(t => `${t.isOdyssey ? 'odyssey' : 'mission_job'}_${t.id}`))
          const allPrevTasksInNew = Array.from(prevTaskKeys).every(key => newTaskKeys.has(key))

          if (allPrevTasksInNew) {
            return uniqueTasks
          }
        }

        // In other cases, use the new set
        return uniqueTasks
      })
    } else {
      if (previousTasksKeyRef.current !== '') {
        previousTasksKeyRef.current = ''
        setCombinedTasks([])
      }
    }
  }, [robot.robotInfo?.robotRaw])

  const getLoadingStatus = (task: CombinedTask) => {
    let loadingStatus = ''
    if (task.status) {
      loadingStatus =
        task.status === 'new' || task.status === 'scenario_error'
          ? 'Not loaded'
          : task.status === 'scenario_done'
          ? 'Loaded'
          : 'Loading'
    }
    return loadingStatus
  }

  const getStartDate = (task: CombinedTask) => {
    let startDate: Date
    if (task.scheduler_settings && task.scheduler_settings.datetime) {
      startDate = new Date(task.scheduler_settings.datetime.replace(' ', 'T'))
    } else {
      startDate = new Date(task.created_at.replace(' ', 'T'))
      const validDateString = task.created_at.slice(0, 16) // Remove extra info
      startDate = new Date(validDateString)
    }
    return startDate
  }
  const getSchedule = (task: CombinedTask) => {
    let schedule = ''
    const days = task.scheduler_settings?.weekdays
    if (days) {
      if (days.SU === 'true') schedule += 'SU '
      if (days.MO === 'true') schedule += 'MO '
      if (days.TU === 'true') schedule += 'TU '
      if (days.WE === 'true') schedule += 'WE '
      if (days.TH === 'true') schedule += 'TH '
      if (days.FR === 'true') schedule += 'FR '
      if (days.SA === 'true') schedule += 'SA '
    }
    return schedule
  }

  const getAdditionalInfo = (task: CombinedTask) => {
    let days = ''
    if (task.scheduler_settings?.except_days) for (const day of task.scheduler_settings.except_days) days += day + ' '

    if (days === '') return ''

    return 'Except days: ' + days
  }

  const setSelectedTaskHandler = async (task: CombinedTask) => {
    if (robot.robotInfo && robot.robotInfo.robotRaw && task.job_type !== MissionJobTypeEnum.DELETE_SCENARIO) {
      setSelectedTask(task)
    } else {
      setSelectedTask(null)
    }
  }

  const handleTaskClick = (task: CombinedTask) => {
    if (!task.isOdyssey) {
      setSelectedTaskHandler(task)
      setIsModalOpen(true)
    }
  }

  const resetData = () => {
    setSelectedTask(null)
    setIsModalOpen(false)
  }
  const onCloseModal = () => {
    setIsModalOpen(true)
    resetData()
  }

  const onCancelClicked = () => {
    onCloseModal()
  }

  const onSaveClicked = async (refresh: boolean) => {
    onCloseModal()
    // Don't call fetchRobot here, as RobotView.tsx already updates data every 3 seconds
    // Periodic update in RobotView will pick up new data
  }

  const handleDeleteClicked = async (task: CombinedTask) => {
    await setSelectedTaskHandler(task)
    setDeleteTaskDialog(true)
  }

  const deleteTask = async () => {
    try {
      if (selectedTask) {
        if (selectedTask.isOdyssey) {
          await RobotApi.updateOdysseyType(selectedTask.id, MissionJobTypeEnum.DELETE_SCENARIO)
        } else {
          await RobotApi.updateMissionJobType(selectedTask.id, MissionJobTypeEnum.DELETE_SCENARIO)
        }
        setDeleteTaskDialog(false)
        setSelectedTask(null)
      }
    } catch (error) {
      console.log('Error deleting tha task: ', error)
    }
  }

  const isNotEditableTask = (task: CombinedTask) => {
    const isToDelete = task.job_type === MissionJobTypeEnum.DELETE_SCENARIO
    const nowDate = new Date()
    const isPassed =
      !task.scheduler_settings?.weekdays &&
      toISOStringYYYYMMDD(
        task.scheduler_settings?.datetime ? new Date(task.scheduler_settings.datetime) : new Date(task.created_at)
      ) <= toISOStringYYYYMMDD(nowDate)
    return isToDelete || isPassed
  }

  return (
    <div className={style.root}>
      <div className={style.tableWrapper}>
        <div className={style.tableHeader}>
          <div className={style.tableHeaderItem}></div>
          <div className={clsx(style.tableHeaderItem, style.centered)}>Task</div>
          <div className={clsx(style.tableHeaderItem, style.centered)}>
            Mission/
            <br />
            Scenario
          </div>
          <div className={style.tableHeaderItem}>Date</div>
          <div className={style.tableHeaderItem}>Schedule</div>
          <div className={clsx(style.tableHeaderItem, style.centered)}>Status</div>
          <div className={style.tableHeaderItem}></div>
        </div>
        <div className={style.table}>
          {[...combinedTasks]
            .sort((a: CombinedTask, b: CombinedTask) => b.created_at.localeCompare(a.created_at))
            .map(task => (
              <div key={`${task.isOdyssey ? 'odyssey' : 'mission_job'}_${task.id}`} className={style.tableRow}>
                <div
                  className={style.tableCol}
                  title={
                    task.job_type === MissionJobTypeEnum.DELETE_SCENARIO
                      ? 'The task will be deleted when the robot connects to the network'
                      : ''
                  }
                >
                  {task.job_type === MissionJobTypeEnum.DELETE_SCENARIO ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <RemoveSvg />
                    </div>
                  ) : (
                    ''
                  )}
                </div>
                <div className={clsx(style.tableCol, style.centered)}>{task.id}</div>
                <div className={clsx(style.tableColWrap, style.centered)}>
                  {task.isOdyssey ? (
                    <div className={style.missionLinksContainer}>
                      {(task as OdysseyRead).scenario ? (
                        <a
                          className={style.link}
                          href={`${PATH.SCENARIOS}${(task as OdysseyRead).scenario}`}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {(task as OdysseyRead).scenario}
                        </a>
                      ) : (
                        (task as OdysseyRead).missions.map((missionId, index) => (
                          <React.Fragment key={missionId}>
                            <a
                              className={style.link}
                              href={`${PATH.MISSION}${missionId}`}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {missionId}
                            </a>
                            {index < (task as OdysseyRead).missions.length - 1 && ', '}
                          </React.Fragment>
                        ))
                      )}
                    </div>
                  ) : (
                    <a
                      className={style.link}
                      href={`${PATH.MISSION}${(task as MissionJobRead).mission_id}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {(task as MissionJobRead).mission_id}
                    </a>
                  )}
                </div>
                <div className={style.tableColWrap}>{getStartDate(task).toLocaleString()}</div>
                <div className={style.tableColWrap} title={getAdditionalInfo(task)}>
                  {getSchedule(task)}
                </div>
                <div className={clsx(style.tableCol, style.centered)}>{getLoadingStatus(task)}</div>
                <div className={style.tableCol} style={{ position: 'relative' }}>
                  <ActionsMenu>
                    <ActionsMenuItem
                      onClick={() => handleTaskClick(task)}
                      data-test-id={getTestId('edit-zone-list')}
                      disabled={isNotEditableTask(task) || task.isOdyssey}
                    >
                      <span className={style.menuItemIcon} />
                      <span className={style.menuItemText} data-test-id={getTestId('edit-title')}>
                        {t('common.edit')}
                      </span>
                    </ActionsMenuItem>
                    <ActionsMenuItem
                      onClick={() => handleDeleteClicked(task)}
                      data-test-id={getTestId('remove-zone-list')}
                      disabled={task.job_type === MissionJobTypeEnum.DELETE_SCENARIO}
                    >
                      <RemoveSvg className={style.menuItemIcon} />
                      <span data-test-id={getTestId('delete-title')}>{t('common.delete')}</span>
                    </ActionsMenuItem>
                  </ActionsMenu>
                </div>
              </div>
            ))}
        </div>
        <TaskSettingsModal
          isOpen={isModalOpen}
          onClose={onCancelClicked}
          taskName={selectedTask?.name ? selectedTask.name : ''}
          taskToEdit={selectedTask && !selectedTask.isOdyssey ? (selectedTask as unknown as MissionJobRead) : undefined}
          eventScope={'all'}
          dateToEdit={null}
          onSubmit={onSaveClicked}
        />

        <ConfirmDialog
          title={selectedTask?.scheduler_settings?.weekdays ? 'Delete the scheduled task?' : 'Delete the task?'}
          isOpen={deleteTaskDialog}
          onClose={() => setDeleteTaskDialog(false)}
          onConfirmClick={deleteTask}
          confirmText={t('common.delete')}
          danger={true}
        />
      </div>
    </div>
  )
}

export default RobotTasks
